const { CivilCardTypes } = require('../Constants/Names/CardTypes');
const { GetCardRowCost, GetIncreasePopulationCost } = require('../Utilities/Utilities');
const { CostKinds, PoolKinds } = require('../Effects/Effects');
const TechnologyTypes = require('../Constants/TechnologyTypes');
const Leaders = require('../Constants/Leaders/Leaders');
const { CardDefinition, WonderDefinition } = require('../Constants/CardVersions');

// A cost: the usual amount, the labelled changes to it (e.g. { source: "Moses", amount: -1 }), the total after them,
// what pools pay of it (pools: [{ source, amount }], e.g. Patriotism's resources for military units; see WithPools) and
// what is left to pay (pay). Changes are applied in order and never take the total below 0; one that changes nothing
// is left out. once: the change can be used once per turn, and is used up when the cost is paid.
const MakeCost = (base, adjustments = []) => {
    let total = base;
    const applied = [];
    adjustments.forEach(adjustment => {
        const amount = Math.max(adjustment.amount, -total);
        if (amount !== 0)
        {
            total += amount;
            applied.push({ ...adjustment, amount });
        }
    });
    return { base, total, adjustments: applied, pools: [], pay: total };
};

// The player's changes of one kind of cost for this context (see Effects.AddCostModifier), skipping once-per-turn
// changes already used this turn, and the discount of an action card being played
const Adjustments = (player, kind, context = {}) => [
    ...player.costModifierDelegate
        .filter(modifier => modifier.kind === kind && !(modifier.once && player.turnFlags.includes(modifier.once)))
        .map(modifier => ({ source: modifier.Source?.name ?? "Unknown", amount: modifier.Action(context), once: modifier.once })),
    ...player.cardDiscounts
        .filter(discount => discount.kind === kind)
        .map(discount => ({ source: discount.source, amount: -discount.amount })),
].filter(adjustment => adjustment.amount !== 0);

// What the player has left this turn in pools of this kind: from cards in play (Homer: 1 each turn) and from this
// turn (Patriotism), as [{ source, amount }]
const AvailablePools = (player, kind) => [
    ...player.resourcePoolDelegate
        .filter(pool => pool.kind === kind)
        .map(pool => {
            const source = pool.Source?.name ?? "Unknown";
            return { source, amount: Math.max(0, pool.Action() - (player.turnPoolUsage[source] ?? 0)), ...(pool.payWith ? { payWith: pool.payWith } : {}) };
        }),
    ...player.turnPools
        .filter(pool => pool.kind === kind)
        .map(pool => ({ source: pool.source, amount: pool.amount, stored: pool })),
].filter(pool => pool.amount > 0);

// The player's choice about pools for a move: { decline: pay without pools of extra resources (true for all of them, or
// by kind: { militaryResources: true }), trade: pay part in
// another currency (Trade Routes Agreement) }. From a move (declinePools, tradeRoute), a choice already made, or, when
// nothing is given, the choice the controller set on the player for the options it lists (player.poolChoice).
const MoveChoice = (move) => typeof move.declinePools === 'object' && move.declinePools !== null
    ? move.declinePools
    : { decline: !!move.declinePools, trade: !!move.tradeRoute };
const PoolChoice = (player, choice) => {
    if (typeof choice === 'object' && choice !== null)
    {
        return choice;
    }
    return choice === undefined ? (player.poolChoice ?? { decline: false, trade: false }) : { decline: !!choice, trade: false };
};
const Has = (player, currency) => currency === 'food' ? player.GetFood() : currency === 'resources' ? player.GetResources() : 0;

// Lets pools of this kind pay what they can of a cost: always, first (temporary resources for a purpose are spent
// before anything else, the rules leave no choice; a move asking to decline them, from before, is not heard); and, when
// the player chooses, a pool paying part of any cost in this currency with another (as far as they have it)
const WithPools = (player, cost, kind, choice, currency) => {
    const { trade } = PoolChoice(player, choice);
    const declined = false;
    let left = cost.total;
    const pools = [];
    [...(declined || !kind ? [] : AvailablePools(player, kind)),
        ...(trade && currency ? AvailablePools(player, currency).map(pool => ({ ...pool, amount: Math.min(pool.amount, Has(player, pool.payWith)) })) : [])
    ].forEach(pool => {
        const amount = Math.min(pool.amount, left);
        if (amount > 0)
        {
            left -= amount;
            pools.push({ ...pool, amount });
        }
    });
    return { ...cost, pools, pay: left };
};

// Uses up the once-per-turn changes and the pool amounts a cost relied on
const PayCost = (player, cost) => {
    cost.adjustments.forEach(adjustment => {
        if (adjustment.once && !player.turnFlags.includes(adjustment.once))
        {
            player.turnFlags.push(adjustment.once);
        }
    });
    cost.pools.forEach(pool => {
        // Paid in another currency instead
        if (pool.payWith === 'food')
        {
            player.PayFood(pool.amount);
        } else if (pool.payWith === 'resources') {
            player.PayResources(pool.amount);
        }
        if (pool.stored)
        {
            const stored = player.turnPools.find(other => other.source === pool.stored.source && other.kind === pool.stored.kind);
            stored.amount -= pool.amount;
        } else {
            player.turnPoolUsage[pool.source] = (player.turnPoolUsage[pool.source] ?? 0) + pool.amount;
        }
    });
};

// Runs fn with a card's discount (or a list of them) in place, to work out or pay the cost of the action that comes
// with it (an action card, an event)
const WithCardDiscount = (player, discount, fn) => {
    const added = [].concat(discount ?? []);
    if (added.length === 0)
    {
        return fn();
    }
    player.cardDiscounts.push(...added);
    try {
        return fn();
    } finally {
        player.cardDiscounts = player.cardDiscounts.filter(other => !added.includes(other));
    }
};

// The pools each cost kind can use: military units can be paid for from military resources, urban buildings from
// resources for wonders and urban buildings (Cleopatra)
const PoolFor = (tech) => isMilitaryType(tech.type) ? PoolKinds.MilitaryResources
    : Object.values(TechnologyTypes.Urban).includes(tech.type) ? PoolKinds.WonderUrbanResources : null;

// Civil actions needed to take a card from a card row slot: the slot's cost, 1 more per completed wonder for a
// wonder, and changes from the player's cards (e.g. Hammurabi) and the card itself (e.g. Taj Mahal)
const GetTakeCardCostDetails = (player, card, slot) => {
    const adjustments = [];
    if (card.type === CivilCardTypes.Wonder && player.wonders.length > 0)
    {
        adjustments.push({ source: "Completed wonders", amount: player.wonders.length });
    }
    // (base: the cost so far, e.g. for a change that may not take it below 1)
    adjustments.push(...Adjustments(player, CostKinds.TakeCard, { card, base: GetCardRowCost(slot) + adjustments.reduce((sum, a) => sum + a.amount, 0) }));
    const ownAdjustment = CardDefinition(card)?.takeCardAdjustment?.(player);
    if (ownAdjustment)
    {
        adjustments.push({ source: card.name, amount: ownAdjustment });
    }
    return MakeCost(GetCardRowCost(slot), adjustments);
};
const GetTakeCardCost = (player, card, slot) => GetTakeCardCostDetails(player, card, slot).total;

// Food to increase population, or null when the yellow bank is empty
const GetPopulationCost = (player, choice = { decline: false, trade: false }) => {
    const base = GetIncreasePopulationCost(player.yellowBank);
    return base === null ? null : WithPools(player, MakeCost(base, Adjustments(player, CostKinds.PopulationFood)), null, choice, PoolKinds.Food);
};

// The resource cost of one building or unit on this card, after discounts on the card's cost (e.g. Masonry)
const BuildCostOf = (player, tech) => MakeCost(tech.workerCost, Adjustments(player, CostKinds.BuildCost, { tech }));

const isMilitaryType = (type) => Object.values(TechnologyTypes.Military).includes(type);

// Discounts on what is paid for building or upgrading (e.g. an action card's)
const PaymentAdjustments = (player, tech) => Adjustments(player, CostKinds.BuildPayment, { tech });

// Resources to build on a card; declinePools: pay it all from resources
const GetBuildCost = (player, tech, declinePools) => {
    const cost = BuildCostOf(player, tech);
    return WithPools(player, MakeCost(tech.workerCost, [...cost.adjustments, ...PaymentAdjustments(player, tech)]),
        PoolFor(tech), declinePools, PoolKinds.Resources);
};

// Resources to upgrade: the difference between the two cards' costs (each after its discounts), then payment discounts
const GetUpgradeCost = (player, from, to, declinePools) => {
    const fromCost = BuildCostOf(player, from);
    const toCost = BuildCostOf(player, to);
    const base = Math.max(0, to.workerCost - from.workerCost);
    // The net change from cost discounts, per source (e.g. Architecture: -2 on Age II, -1 on Age I gives -1)
    const bySource = new Map();
    toCost.adjustments.forEach(a => bySource.set(a.source, (bySource.get(a.source) ?? 0) + a.amount));
    fromCost.adjustments.forEach(a => bySource.set(a.source, (bySource.get(a.source) ?? 0) - a.amount));
    const discounts = [...bySource].map(([source, amount]) => ({ source, amount }));
    return WithPools(player, MakeCost(base, [...discounts, ...PaymentAdjustments(player, to)]), PoolFor(to), declinePools, PoolKinds.Resources);
};

// Science to develop a technology
const GetDevelopCost = (player, tech, declinePools) => WithPools(player,
    MakeCost(tech.scienceCost, Adjustments(player, CostKinds.DevelopScience, { tech })),
    isMilitaryType(tech.type) ? PoolKinds.MilitaryScience : null, declinePools);

// What pools paid of a cost, e.g. "2 from Patriotism"; empty when none did
const PoolText = (cost) => cost.pools.map(pool => pool.payWith
    ? `${pool.amount} paid with ${pool.payWith} (${pool.source})`
    : `${pool.amount} from ${pool.source}`).join(', ');

// What part of a cost is paid in another currency instead (Trade Routes Agreement), e.g. { food: 1 }
const SwappedPart = (cost) => cost.pools.filter(pool => pool.payWith)
    .reduce((parts, pool) => ({ ...parts, [pool.payWith]: (parts[pool.payWith] ?? 0) + pool.amount }), {});

// The changes to a cost as short text, e.g. "-1 Moses, +1 Completed wonders"; empty when there are none
const AdjustmentText = (cost) => cost.adjustments
    .map(adjustment => `${adjustment.amount > 0 ? '+' : '−'}${Math.abs(adjustment.amount)} ${adjustment.source}`)
    .join(', ');

// The error for not having enough civil or military actions left. Moves check this last, so an option that
// fails only this way is otherwise legal and can be shown greyed out instead of hidden.
const ActionShortage = (kind, count) => `Needs ${count} ${kind} action${count === 1 ? "" : "s"}`;
const IsActionShortage = (error) => /^Needs \d+ (civil|military) actions?$/.test(error ?? "");

// Wonder stages per civil action: 1, or more with a construction technology (Masonry 2, Architecture 3, ...)
const WonderStagesPerAction = (player) => Math.max(1, ...player.technologies.map(tech => tech.wonderStages ?? 1));

// Total resources for the next 'stages' stages of the unfinished wonder
const WonderStagesCost = (wonder, stages) => wonder
    ? wonder.card.stageCosts.slice(wonder.stagesBuilt, wonder.stagesBuilt + stages).reduce((sum, cost) => sum + cost, 0)
    : 0;

// The same, with discounts (e.g. Engineering Genius)
// (a wonder paid in food, the International Red Cross: its cost as it is, in food: see WonderCurrency)
const GetWonderStagesCost = (player, stages, choice) => WonderCurrency(player.wonderInProgress) === 'food'
    ? MakeCost(WonderStagesCost(player.wonderInProgress, stages))
    : WithPools(player, MakeCost(WonderStagesCost(player.wonderInProgress, stages), Adjustments(player, CostKinds.WonderPayment)), PoolKinds.WonderUrbanResources, choice, PoolKinds.Resources);

// What a wonder's stages are paid in: 'resources', or 'food' (the International Red Cross)
const WonderCurrency = (wonder) => (wonder && WonderDefinition(wonder.card)?.payWith) ?? 'resources';

// A card taken from the card row this turn cannot be played until a later turn
const TakenThisTurn = "Cannot be played the turn it was taken";

// A revolution takes every civil action (or military action, with Robespierre), so none may be spent yet
const RevolutionNeedsAllActions = (kind) => `A revolution needs all your ${kind} actions unspent`;
const IsRevolutionBlocked = (error) => /^A revolution needs all your (civil|military) actions unspent$/.test(error ?? "");

// Options that are offered greyed out (with the reason) instead of hidden: blocked only by too few actions, or by
// waiting for the next turn
const IsShownDisabled = (error) => IsActionShortage(error) || error === TakenThisTurn || IsRevolutionBlocked(error);

// Science to change to a government: its full cost peacefully, or its revolution cost (less what the event or card the
// revolution comes with takes off developing, e.g. Development of Civilization's 1 science)
const GetGovernmentCost = (player, tech, revolution) => revolution
    ? MakeCost(tech.revolutionCost, player.cardDiscounts.filter(discount => discount.kind === CostKinds.DevelopScience)
        .map(discount => ({ source: discount.source, amount: -discount.amount })))
    : GetDevelopCost(player, tech);

// Military cards above the hand limit (normally the military action total), to be discarded at the end of the turn
const MilitaryCardsOverLimit = (player) => Math.max(0, player.militaryHand.length - player.GetMilitaryHandLimit());

module.exports = {
    WonderCurrency,
    MakeCost,
    PayCost,
    WithCardDiscount,
    AvailablePools,
    PoolText,
    GetWonderStagesCost,
    TakenThisTurn,
    RevolutionNeedsAllActions,
    GetGovernmentCost,
    IsShownDisabled,
    AdjustmentText,
    GetTakeCardCost,
    GetTakeCardCostDetails,
    GetPopulationCost,
    MoveChoice,
    SwappedPart,
    GetBuildCost,
    GetUpgradeCost,
    BuildCostOf,
    PaymentAdjustments,
    GetDevelopCost,
    MilitaryCardsOverLimit,
    WonderStagesPerAction,
    WonderStagesCost,
    ActionShortage,
    IsActionShortage,
}
